/**
 * Utility functions for the frontend
 */

import { format, formatDistanceToNow, isPast, isFuture, differenceInHours } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const SQLITE_DATETIME_UTC_RE = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/;
const ISO_DATETIME_NO_TZ_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

function isValidDateValue(d: Date): boolean {
  return !Number.isNaN(d.getTime());
}

/**
 * Parse common backend date/time formats into a Date.
 *
 * Supported inputs:
 * - ISO 8601 with timezone (e.g. 2026-06-11T17:00:00Z)
 * - SQLite-style UTC timestamp (e.g. 2026-06-11 17:00:00)
 * - Date-only "day key" (YYYY-MM-DD) → parsed as local midnight
 *
 * Why: `new Date("YYYY-MM-DD HH:mm:ss")` is invalid in Safari, and
 * `new Date("YYYY-MM-DD")` is parsed as UTC (can shift the local day).
 */
export function parseDateTimeInput(value: string | Date): Date {
  if (value instanceof Date) return value;

  const s = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return parseLocalDayKey(s);
  }

  // Backend stores many timestamps as SQLite-style UTC strings.
  if (SQLITE_DATETIME_UTC_RE.test(s)) {
    return new Date(s.replace(' ', 'T') + 'Z');
  }

  // If an ISO datetime lacks timezone, treat it as UTC to avoid env-dependent parsing.
  if (ISO_DATETIME_NO_TZ_RE.test(s)) {
    return new Date(s + 'Z');
  }

  return new Date(s);
}

/**
 * Convert a date/time value into a stable local "day key" (YYYY-MM-DD).
 *
 * Why: we store many timestamps as SQLite-style strings (e.g. "2026-06-11 17:00:00")
 * and some as ISO strings. Grouping by string slicing (split('T')) is fragile and
 * can be wrong across timezones (Date("YYYY-MM-DD") is parsed as UTC).
 */
export function getLocalDayKey(date: string | Date): string {
  const d = parseDateTimeInput(date);
  if (!isValidDateValue(d)) return 'invalid-date';
  return format(d, 'yyyy-MM-dd');
}

/**
 * Parse a local day key (YYYY-MM-DD) to a Date at local midnight.
 * Avoids the UTC parsing pitfall of `new Date("YYYY-MM-DD")`.
 */
export function parseLocalDayKey(dayKey: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey);
  if (!match) return new Date(NaN);
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  return new Date(year, monthIndex, day);
}

/**
 * Format a date for display
 */
export function formatDate(date: string | Date, pattern: string = 'dd/MM/yyyy'): string {
  const d = parseDateTimeInput(date);
  if (!isValidDateValue(d)) return '-';
  return format(d, pattern, { locale: ptBR });
}

/**
 * Format date with time
 */
export function formatDateTime(date: string | Date): string {
  const d = parseDateTimeInput(date);
  if (!isValidDateValue(d)) return '-';
  return format(d, "dd/MM/yyyy 'as' HH:mm", { locale: ptBR });
}

/**
 * Format time only
 */
export function formatTime(date: string | Date): string {
  const d = parseDateTimeInput(date);
  if (!isValidDateValue(d)) return '-';
  return format(d, 'HH:mm', { locale: ptBR });
}

/**
 * Format relative time (e.g., "em 2 horas", "ha 3 dias")
 */
export function formatRelative(date: string | Date): string {
  const d = parseDateTimeInput(date);
  if (!isValidDateValue(d)) return '-';
  return formatDistanceToNow(d, { addSuffix: true, locale: ptBR });
}

/**
 * Check if a date is in the past
 */
export function isDatePast(date: string | Date): boolean {
  const d = parseDateTimeInput(date);
  if (!isValidDateValue(d)) return false;
  return isPast(d);
}

/**
 * Check if a date is in the future
 */
export function isDateFuture(date: string | Date): boolean {
  const d = parseDateTimeInput(date);
  if (!isValidDateValue(d)) return false;
  return isFuture(d);
}

/**
 * Get hours until a date (for countdown)
 */
export function getHoursUntil(date: string | Date): number {
  const d = parseDateTimeInput(date);
  if (!isValidDateValue(d)) return 0;
  return differenceInHours(d, new Date());
}

/**
 * Check if match is locked for predictions
 */
export function isMatchLocked(lockTime: string | Date): boolean {
  const d = parseDateTimeInput(lockTime);
  if (!isValidDateValue(d)) return true; // Fail closed in UI as well.
  return Date.now() >= d.getTime();
}

/**
 * Format match date for display
 */
export function formatMatchDate(date: string | Date): string {
  const d = parseDateTimeInput(date);
  if (!isValidDateValue(d)) return '-';
  return format(d, "EEEE, dd 'de' MMMM", { locale: ptBR });
}

/**
 * Get stage display name
 */
export function getStageDisplayName(stage: string): string {
  const stageNames: Record<string, string> = {
    GROUP_A: 'Grupo A',
    GROUP_B: 'Grupo B',
    GROUP_C: 'Grupo C',
    GROUP_D: 'Grupo D',
    GROUP_E: 'Grupo E',
    GROUP_F: 'Grupo F',
    GROUP_G: 'Grupo G',
    GROUP_H: 'Grupo H',
    GROUP_I: 'Grupo I',
    GROUP_J: 'Grupo J',
    GROUP_K: 'Grupo K',
    GROUP_L: 'Grupo L',
    ROUND_32: 'Oitavas de Final',
    ROUND_16: 'Oitavas de Final',
    QUARTER: 'Quartas de Final',
    SEMI: 'Semifinal',
    THIRD_PLACE: 'Disputa pelo 3o lugar',
    FINAL: 'Final',
  };

  return stageNames[stage] || stage;
}

/**
 * Get match status display
 */
export function getStatusDisplay(status: string): { label: string; color: string } {
  const statusMap: Record<string, { label: string; color: string }> = {
    scheduled: { label: 'Agendado', color: 'bg-blue-100 text-blue-800' },
    live: { label: 'Ao Vivo', color: 'bg-red-100 text-red-800' },
    finished: { label: 'Encerrado', color: 'bg-gray-100 text-gray-800' },
    postponed: { label: 'Adiado', color: 'bg-yellow-100 text-yellow-800' },
    cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-800' },
  };

  return statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
}

/**
 * Get prediction result display
 */
export function getPredictionResultDisplay(result: string): { label: string; color: string; points: number } {
  const resultMap: Record<string, { label: string; color: string; points: number }> = {
    exact: { label: 'Placar Exato', color: 'bg-green-100 text-green-800', points: 10 },
    diff: { label: 'Saldo + Vencedor', color: 'bg-blue-100 text-blue-800', points: 7 },
    winner: { label: 'Vencedor', color: 'bg-yellow-100 text-yellow-800', points: 3 },
    none: { label: 'Errou', color: 'bg-red-100 text-red-800', points: 0 },
    pending: { label: 'Aguardando', color: 'bg-gray-100 text-gray-800', points: 0 },
  };

  return resultMap[result] || { label: result, color: 'bg-gray-100 text-gray-800', points: 0 };
}

/**
 * Get trend display
 */
export function getTrendDisplay(trend: string): { icon: string; color: string; label: string } {
  const trendMap: Record<string, { icon: string; color: string; label: string }> = {
    up: { icon: '↑', color: 'text-green-500', label: 'Subiu' },
    down: { icon: '↓', color: 'text-red-500', label: 'Desceu' },
    same: { icon: '=', color: 'text-gray-400', label: 'Manteve' },
    new: { icon: '★', color: 'text-blue-500', label: 'Novo' },
  };

  return trendMap[trend] || { icon: '-', color: 'text-gray-400', label: trend };
}

/**
 * Format position for display (1st, 2nd, 3rd, etc.)
 */
export function formatPosition(position: number): string {
  if (position === 1) return '1o';
  if (position === 2) return '2o';
  if (position === 3) return '3o';
  return `${position}o`;
}

/**
 * Get initials from name
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/**
 * Truncate text
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Format points for display
 */
export function formatPoints(points: number): string {
  return `${points} pt${points !== 1 ? 's' : ''}`;
}

/**
 * Calculate percentage
 */
export function calculatePercentage(value: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((value / total) * 100);
}

/**
 * Class name merger (like clsx/cn)
 */
export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Format bytes to human readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
